import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { subscribeToPush, isSubscribed, pushSupported } from "@/lib/push";
import { Truck, Mail, Phone, IdCard, Car, LogOut, Bell, BellRing, Loader2 } from "lucide-react";

export default function DriverProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [subbed, setSubbed] = useState(false);
  const [busy, setBusy] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };

  useEffect(() => { isSubscribed().then(setSubbed).catch(() => {}); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setSubbed(true);
      toast.success("Push alerts enabled — your phone will buzz on new deliveries");
    } catch (e) {
      toast.error(e.message || "Could not enable alerts");
    } finally { setBusy(false); }
  };

  const rows = [
    { icon: Mail, label: "Email", value: user?.email },
    { icon: Phone, label: "Mobile", value: user?.phone },
    { icon: IdCard, label: "Driver ID", value: user?.employee_id },
    { icon: Car, label: "Vehicle", value: user?.vehicle },
    { icon: Truck, label: "Registration", value: user?.registration },
  ];

  return (
    <div className="animate-slide-up">
      <div className="rounded-2xl bg-[#0B192C] p-6 text-center text-white">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FF6500] text-2xl font-black">
          {user?.name?.[0]}
        </div>
        <h1 className="mt-3 font-head text-xl font-extrabold">{user?.name}</h1>
        <p className="text-sm capitalize text-slate-400">{user?.role}</p>
      </div>

      {/* Push alerts */}
      <div className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="push-alerts-card">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${subbed ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
          {subbed ? <BellRing size={22} /> : <Bell size={22} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#0B192C]">Delivery Push Alerts</div>
          <div className="text-xs text-slate-500">{subbed ? "On — your phone buzzes on new deliveries" : "Get buzzed when a new delivery is assigned"}</div>
        </div>
        {subbed ? (
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">Enabled</span>
        ) : (
          <button onClick={enable} disabled={busy || !pushSupported()} data-testid="enable-push-btn" className="flex items-center gap-1.5 rounded-xl bg-[#FF6500] px-3 py-2 text-sm font-bold text-white tap-shrink disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Enable"}
          </button>
        )}
      </div>

      <div className="mt-4 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3 px-4 py-3">
            <r.icon size={18} className="text-slate-400" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-slate-400">{r.label}</div>
              <div className="truncate font-semibold text-[#0B192C]">{r.value || "-"}</div>
            </div>
          </div>
        ))}
      </div>

      <button onClick={doLogout} data-testid="profile-logout-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 py-3 font-bold text-rose-700 tap-shrink">
        <LogOut size={18} /> Sign out
      </button>
    </div>
  );
}
